import React from 'react';

/**
 * Lightweight, dependency-free cartoon art for the kid UI.
 * Inline SVG + emoji stickers keep it themeable, crisp at any size, and
 * avoids bundling external image assets / licensing concerns.
 */

const WORDMARK_COLORS = ['#EF4444', '#F97316', '#FACC15', '#22C55E', '#3B82F6', '#6366F1', '#A855F7', '#EC4899', '#14B8A6'];

/** Rainbow "KidsTrack" wordmark — each letter individually colored. */
export const KidsTrackWordmark: React.FC<{ className?: string }> = ({ className = '' }) => {
  const text = 'KidsTrack';
  return (
    <span className={`font-display font-extrabold tracking-tight ${className}`} aria-label="KidsTrack">
      {text.split('').map((ch, i) => (
        <span key={i} style={{ color: WORDMARK_COLORS[i % WORDMARK_COLORS.length] }}>
          {ch}
        </span>
      ))}
    </span>
  );
};

/** A friendly waving kid mascot drawn with basic shapes. */
export const HeroKid: React.FC<{ size?: number; className?: string }> = ({ size = 96, className = '' }) => (
  <svg viewBox="0 0 120 120" width={size} height={size} className={className} aria-hidden="true">
    {/* sparkle */}
    <g fill="#FDE68A">
      <circle cx="104" cy="22" r="3" />
      <circle cx="96" cy="34" r="2" />
      <circle cx="112" cy="34" r="2" />
    </g>
    {/* lightbulb idea */}
    <g transform="translate(92 12)">
      <circle cx="6" cy="6" r="7" fill="#FCD34D" />
      <rect x="3.5" y="11" width="5" height="4" rx="1" fill="#9CA3AF" />
    </g>
    {/* body */}
    <rect x="42" y="74" width="36" height="34" rx="14" fill="#FBBF24" />
    {/* waving arm */}
    <path d="M78 80 Q98 70 96 50" stroke="#F4A98B" strokeWidth="9" strokeLinecap="round" fill="none" />
    <circle cx="96" cy="48" r="7" fill="#F4A98B" />
    {/* other arm */}
    <path d="M44 82 Q34 92 38 102" stroke="#F4A98B" strokeWidth="9" strokeLinecap="round" fill="none" />
    {/* head */}
    <circle cx="60" cy="54" r="24" fill="#F7C9A9" />
    {/* hair */}
    <path d="M36 50 Q40 26 60 26 Q80 26 84 50 Q72 40 60 42 Q48 40 36 50 Z" fill="#6B4226" />
    <circle cx="40" cy="40" r="9" fill="#6B4226" />
    <circle cx="80" cy="40" r="9" fill="#6B4226" />
    {/* face */}
    <circle cx="52" cy="54" r="3" fill="#3B2A1A" />
    <circle cx="68" cy="54" r="3" fill="#3B2A1A" />
    <path d="M53 63 Q60 69 67 63" stroke="#B45309" strokeWidth="2.5" strokeLinecap="round" fill="none" />
    <circle cx="46" cy="61" r="3" fill="#FCA5A5" opacity="0.7" />
    <circle cx="74" cy="61" r="3" fill="#FCA5A5" opacity="0.7" />
  </svg>
);

/** Emoji "sticker" inside a soft gradient disc — the reusable cartoon accent. */
export const Sticker: React.FC<{
  emoji: string;
  from?: string;
  to?: string;
  size?: number;
  className?: string;
}> = ({ emoji, from = '#C7D2FE', to = '#DDD6FE', size = 56, className = '' }) => (
  <span
    className={`inline-flex items-center justify-center rounded-2xl shadow-sm shrink-0 ${className}`}
    style={{
      width: size,
      height: size,
      fontSize: size * 0.5,
      background: `linear-gradient(135deg, ${from}, ${to})`,
    }}
    aria-hidden="true"
  >
    {emoji}
  </span>
);
